import React, { useEffect, useRef, useState } from 'react'
import Section from '../Section'
import SuccessStoriesCard from '../SuccessStoriesCard'
import Announcer, { useAnnouncer, fillPlaceholders } from '../Announcer'

interface SuccessStoryObj {
    id: string
    title: string
    tagline_heading?: string
    pageUrl: string
    image?: {
        imageUrl: string
        data?: { altText?: string }
    }
}

interface props {
    title?: string
    endpoint: string
    highlightedSuccessStory?: {
        title: string
        taglineHeading: string
        url: string
        imgSrc: string,
        imgSrcSmall: string,
        alt: string
    }
    firstPageStories: SuccessStoryObj[]
    hasSecondPage: boolean
    pageNumber?: number
    strings: {
        loadMore: string // Load more
        loading: string // Loading...
        resultsStatus: string // Success stories: showing <<shown>>
    }
}

function SuccessStoriesOverview({ title, endpoint, highlightedSuccessStory, firstPageStories, hasSecondPage, pageNumber, strings }: props) {
    const [stories, setStories] = useState<SuccessStoryObj[]>(firstPageStories || [])
    const [hasNextPage, setHasNextPage] = useState<boolean>(!!hasSecondPage)
    const [currentPage, setCurrentPage] = useState<number>(pageNumber && pageNumber > 1 ? pageNumber : 1)
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
    const [announcement, announce] = useAnnouncer()

    const resultsRef = useRef<HTMLDivElement>(null)
    // state lags a render behind, so a held Space could append the same page twice
    const isLoadingMoreRef = useRef<boolean>(false)
    const focusIndexRef = useRef<number | null>(null)

    async function loadMoreItems() {
        if (isLoadingMoreRef.current) return
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        try {
            const serviceRes = await fetch(endpoint + `&page=${currentPage + 1}`)
            if (!serviceRes.ok) throw new Error(`Success stories request failed with status ${serviceRes.status}`)
            const serviceResJson = await serviceRes.json()

            if (serviceResJson.hits) {
                const firstNewIndex = stories.length

                setStories(prevState => [...prevState, ...serviceResJson.hits])
                setHasNextPage(serviceResJson.hasNextPage)
                setCurrentPage(prevState => prevState + 1)

                // the control is about to be removed; focus would fall to <body>
                if (!serviceResJson.hasNextPage) focusIndexRef.current = firstNewIndex

                announce(fillPlaceholders(strings.resultsStatus, { shown: firstNewIndex + serviceResJson.hits.length }))
            }
        } catch (err) {
            console.error(err)
            // the server renders the next page on its own, so fall back to the link
            window.location.href = nextPageHref
        } finally {
            isLoadingMoreRef.current = false
            setIsLoadingMore(false)
        }
    }

    useEffect(() => {
        const index = focusIndexRef.current
        if (index === null) return
        focusIndexRef.current = null

        const firstNewCard = resultsRef.current?.children[index] as HTMLElement
        firstNewCard?.querySelector('a')?.focus()
    }, [stories])

    const nextPageHref = '?page=' + (currentPage + 1)

    const hasModifier = (event) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

    const activateLoadMore = (event) => {
        if (hasModifier(event)) return
        event.preventDefault()
        loadMoreItems()
    }

    const handleLoadMoreKeyDown = (event) => {
        if (event.key !== ' ' && event.key !== 'Spacebar') return
        if (hasModifier(event)) return
        event.preventDefault()
        if (event.repeat) return
        loadMoreItems()
    }

    return (
        <>
            <Section
                textColor='text-white'
                bgColor='bg-primary'
                className='top-section'
            >
                <div className='grid-container'>
                    <h1 className='title-lg title'>
                        {title}
                    </h1>
                </div>
            </Section>

            {/* paginated pages must not share page one's featured card */}
            {(highlightedSuccessStory && !(pageNumber > 1)) ? <Section
                textColor='text-light'
                bgColor='bg-primary'
                className='featured-stories'
            >
                <div className='grid-container'>
                    <SuccessStoriesCard
                        featured={true}
                        href={{
                            url: highlightedSuccessStory.url,
                            isExternal: false
                        }}
                        img={highlightedSuccessStory.imgSrc && {
                            src: highlightedSuccessStory.imgSrc,
                            srcSmall: highlightedSuccessStory.imgSrcSmall,
                            alt: highlightedSuccessStory.alt,
                        }}
                        subtitle={highlightedSuccessStory.taglineHeading}
                        title={highlightedSuccessStory.title}
                    />
                </div>
            </Section> : <></>}

            <Section
                textColor='text-light'
                bgColor='bg-primary'
                className='all-stories'
                ariaBusy={isLoadingMore}
            >
                <div className='grid-container'>

                    <Announcer message={announcement} />

                    {stories?.length > 0 ? (
                        <div className='grid-row' ref={resultsRef}>
                            {stories.map((item, i) => (
                                <div className='c-sm-6 c-lg-4' key={i}>
                                    <SuccessStoriesCard
                                        featured={false}
                                        href={{
                                            url: item.pageUrl,
                                        }}
                                        img={item.image?.imageUrl && {
                                            src: item.image.imageUrl,
                                            alt: item.image.data?.altText
                                        }}
                                        subtitle={item.tagline_heading}
                                        title={item.title}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : <></>}

                    {hasNextPage ? (
                        <div className='load-more'>
                            <a
                                href={nextPageHref}
                                role='button'
                                onClick={activateLoadMore}
                                onKeyDown={handleLoadMoreKeyDown}
                                aria-busy={isLoadingMore ? true : undefined}
                            >
                                {isLoadingMore ? strings.loading : <>
                                    {strings.loadMore} <svg width='14' height='14' fill='currentColor' viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.6998 0.800049V6.30005H13.1998V7.70005H7.6998V13.2H6.2998V7.70005H0.799805V6.30005H6.2998V0.800049H7.6998Z' /></svg>
                                </>}
                            </a>
                        </div>
                    ) : <></>}

                </div>
            </Section>
        </>
    )
}

export default SuccessStoriesOverview